/**
 * FoamClub — "Monte seu Kit".
 * Seleção livre: cada produto tem um stepper de quantidade (0..N).
 * Adiciona ao carrinho exatamente o que foi marcado via /cart/add.js e
 * integra com o cart drawer do Horizon pelo mesmo contrato do product-form:
 * CartLinesUpdateEvent com promise deferida + sections para o morph.
 */

import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * @param {HTMLElement} root
 * @param {string} message
 */
function setStatus(root, message) {
  const status = root.querySelector('[data-foam-bundle-status]');
  if (!(status instanceof HTMLElement)) return;
  status.hidden = !message;
  status.textContent = message;
}

/**
 * @param {HTMLInputElement} input
 */
function clampQty(input) {
  const min = Number(input.min) || 0;
  const max = Number(input.max) || Infinity;
  const value = Math.floor(Number(input.value));
  input.value = String(Math.min(max, Math.max(min, Number.isFinite(value) ? value : 0)));
}

/**
 * @param {HTMLElement} root
 * @returns {{ id: number, quantity: number }[]}
 */
function selectedItems(root) {
  return Array.from(root.querySelectorAll('[data-foam-bundle-qty]'))
    .filter((input) => input instanceof HTMLInputElement)
    .map((input) => ({
      id: Number(/** @type {HTMLInputElement} */ (input).dataset.variantId),
      quantity: Number(/** @type {HTMLInputElement} */ (input).value) || 0,
    }))
    .filter((item) => item.id && item.quantity > 0);
}

/**
 * @param {HTMLElement} root
 */
function updateState(root) {
  let total = 0;
  root.querySelectorAll('[data-foam-bundle-item]').forEach((item) => {
    if (!(item instanceof HTMLElement)) return;
    const input = item.querySelector('[data-foam-bundle-qty]');
    const qty = input instanceof HTMLInputElement ? Number(input.value) || 0 : 0;
    item.classList.toggle('is-selected', qty > 0);
    total += qty;
  });

  const count = root.querySelector('[data-foam-bundle-count]');
  if (count instanceof HTMLElement) {
    if (total === 0) {
      count.textContent = root.dataset.textCountZero || '';
    } else if (total === 1) {
      count.textContent = root.dataset.textCountOne || '';
    } else {
      count.textContent = (root.dataset.textCountOther || '').replace('[count]', String(total));
    }
  }

  const submit = root.querySelector('[data-foam-bundle-submit]');
  if (submit instanceof HTMLButtonElement) submit.disabled = total === 0;
}

/**
 * @param {HTMLElement} root
 * @param {HTMLButtonElement} button
 */
async function addBundle(root, button) {
  const items = selectedItems(root);
  if (items.length === 0) return;

  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
    .filter(Boolean);

  button.disabled = true;
  setStatus(root, root.dataset.textAdding || '');

  const deferred = CartLinesUpdateEvent.createPromise();
  root.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sectionIds.join(',') }),
    });
    const data = await response.json();
    if (data.status) throw new Error(data.message || 'add failed');

    const cart = await fetch(`${Theme.routes.cart_url}.js`).then((res) => res.json());

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'foam-bundle-builder',
        sourceId: 'foam-bundle-builder',
        itemCount: items.length,
        sections: data.sections,
        didError: false,
      },
    });

    setStatus(root, root.dataset.textAdded || '');
    root.querySelectorAll('[data-foam-bundle-qty]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.value = '0';
    });
  } catch (error) {
    deferred.reject(error);
    setStatus(root, root.dataset.textError || '');
  } finally {
    updateState(root);
  }
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const step = event.target.closest('[data-foam-bundle-step]');
  if (step instanceof HTMLButtonElement) {
    const stepper = step.closest('.foam-bundle-item__stepper');
    const input = stepper?.querySelector('[data-foam-bundle-qty]');
    const root = step.closest('[data-foam-bundle]');
    if (input instanceof HTMLInputElement && root instanceof HTMLElement) {
      input.value = String((Number(input.value) || 0) + Number(step.dataset.foamBundleStep));
      clampQty(input);
      updateState(root);
    }
    return;
  }

  const button = event.target.closest('[data-foam-bundle-submit]');
  if (!(button instanceof HTMLButtonElement)) return;
  const root = button.closest('[data-foam-bundle]');
  if (root instanceof HTMLElement) addBundle(root, button);
});

document.addEventListener('input', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.hasAttribute('data-foam-bundle-qty')) return;
  clampQty(input);
  const root = input.closest('[data-foam-bundle]');
  if (root instanceof HTMLElement) updateState(root);
});

document.querySelectorAll('[data-foam-bundle]').forEach((root) => {
  if (root instanceof HTMLElement) updateState(root);
});
